import {
  getErrorMessage,
  getHttpStatus,
  isNetworkFailure,
  logFeatureEvent,
  reportFeatureIssue,
  type ObservabilityScope,
} from "./shared";

type AnalyticsSource = "bot_traffic";

type AnalyticsAttributes = {
  source?: AnalyticsSource;
  period?: string | null;
  httpStatus?: number | null;
};

const attributesFor = ({ source, period, httpStatus }: AnalyticsAttributes = {}) => ({
  analytics_source: source ?? null,
  period: period ?? null,
  http_status: httpStatus ?? null,
});

const logBotTrafficEvent = (
  event: "comparison_loaded" | "no_data" | "load_degraded",
  scope: ObservabilityScope,
  attributes?: AnalyticsAttributes,
  options?: { dedupKey?: string },
) =>
  logFeatureEvent({
    feature: "analytics",
    event,
    scope,
    attributes: attributesFor({ source: "bot_traffic", ...attributes }),
    dedupKey: options?.dedupKey,
  });

/**
 * The Bot Traffic page previously swallowed comparison-fetch failures into
 * `console.error` + an empty state, so a broken bot-classification feed looked
 * identical to a property with no bot traffic. Failures with an HTTP response
 * still create Issues; response-less browser/network failures are logged as
 * degraded loads because they are commonly transient client conditions.
 */
export const reportBotTrafficFailure = (
  error: unknown,
  scope: ObservabilityScope,
  attributes?: AnalyticsAttributes,
) => {
  const httpStatus = getHttpStatus(error);

  if (isNetworkFailure(error)) {
    logFeatureEvent({
      feature: "analytics",
      event: "load_degraded",
      level: "warn",
      message: "Analytics: bot traffic comparison load degraded",
      scope,
      attributes: {
        ...attributesFor({ source: "bot_traffic", ...attributes }),
        error_message: getErrorMessage(error),
      },
      dedupKey: `load-degraded:bot_traffic:${scope.propertyId ?? "unknown"}`,
    });
    return;
  }

  reportFeatureIssue({
    feature: "analytics",
    error,
    action: "load_bot_traffic_comparison",
    message: "Analytics: bot traffic comparison load failed",
    scope,
    context: attributesFor({ source: "bot_traffic", ...attributes, httpStatus }),
    dedupKey: `load-failed:bot_traffic:${scope.propertyId ?? "unknown"}`,
  });
};
